import React from 'react';
import NProgress from 'nprogress';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { DocumentModel, Multimedia, Product, Variant } from '../../../models';
import { FormButtons } from '../../shared/Forms';
import { useNotify } from '../../../hooks';
import { validateUpdateVariantForm } from '../../../lib/formValidations';
import { UPDATE_VARIANT } from '../../../graphql/mutation';
import { removeProperties } from '../../../lib/removeProperties';
import UpdateVariantFormContent from './UpdateVariantFormContent';
import DocumentForm from '../../shared/document/DocumentForm';

interface UpdateVariantFormProps {
  variant?: Variant;
  product?: Product;
}

function UpdateVariantForm({ variant, product }: UpdateVariantFormProps) {
  const notify = useNotify();
  const router = useRouter();

  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<Variant>>(
    variant ?? {}
  );
  const [documents, setDocuments] = React.useState<
    Array<Partial<DocumentModel>>
  >(
    (variant.photos as Multimedia[])?.length > 0
      ? [...variant.photos.map(({ src }) => ({ src }))]
      : []
  );

  const [updateVariantMutation] = useMutation<{
    updateVariant: Variant;
  }>(UPDATE_VARIANT);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const error = validateUpdateVariantForm({
        action: 'update',
        variant: formState,
        product,
      });
      if (error) return notify(error, 'error');
      // build object
      const formStateUpdated = removeProperties(formState, [
        'electronicCodes',
        'codesAutogenerated',
      ]);
      const variantData = {
        ...formStateUpdated,
        stock: Number(formStateUpdated.stock),
        price: Number(formStateUpdated.price),
        compareAtPrice: Number(formStateUpdated.compareAtPrice),
        agreedPrice: Number(formStateUpdated.agreedPrice),
      } as Variant;

      await updateVariant(variantData);
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const updateVariant = async (variantData: Partial<Variant>) => {
    delete variantData.product;
    delete variantData.stock;
    const { data } = await updateVariantMutation({
      variables: {
        data: {
          ...variantData,
          _id: variant._id,
          option: {
            ...variantData.option,
            __typename: undefined,
          },
          photos: documents.map(({ src }) => ({ src })),
          __typename: undefined,
        },
      },
    });
    if (data?.updateVariant?._id) {
      notify('Variante actualizada con éxito', 'success');
      router.push(`/app/products/${product._id}?tab=variants`);
    } else {
      notify('Ha ocurrido un error al actualizar la variante', 'error');
    }
  };

  const changeVariantStatus = async () => {
    try {
      const { data } = await updateVariantMutation({
        variables: {
          data: {
            _id: variant._id,
            active: !variant.active,
          },
        },
      });
      if (data?.updateVariant?._id) {
        notify('Variante actualizada con éxito', 'success');
        router.push(`/app/products/${product._id}?tab=variants`);
      } else {
        notify('Ha ocurrido un error al actualizar la variante', 'error');
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <form>
      <UpdateVariantFormContent
        formState={formState}
        setFormState={setFormState}
        variant={variant}
        product={product}
      />
      <div className="mt-5">
        <DocumentForm
          title="Imagenes de la variante"
          documents={documents}
          updateURLs={setDocuments}
        />
      </div>
      <div className="flex justify-end">
        <FormButtons
          type={variant ? 'update' : 'create'}
          toggleActive={changeVariantStatus}
          active={variant?.active}
          disabled={disabled}
          onSubmit={onSubmit}
        />
      </div>
    </form>
  );
}

export default UpdateVariantForm;
